import { useMemo, useState } from 'react';
import { AssetIcon } from '../components/AssetBadge';
import { IconExternal } from '../components/Icons';
import { useModals } from '../components/modals';
import { Alert, Paper, TopBand } from '../components/ui';
import { useHistory, type HistoryAction, type HistoryItem } from '../data/history';
import { explorerTx } from '../data/tx';
import { fmtToken } from '../lib/format';
import { useWallet } from '../wallet/WalletProvider';

const FILTERS: ('All' | HistoryAction)[] = ['All', 'Supply', 'Withdraw', 'Borrow', 'Repay', 'Liquidation'];
const SIGN: Partial<Record<HistoryAction, string>> = { Supply: '+', Repay: '+', Withdraw: '−', Borrow: '−', Liquidation: '−' };
const day = (ts: number) => new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(ts * 1000);
const time = (ts: number) => new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(ts * 1000);

/** The whole row opens the transaction on the explorer; the View chip only marks it (a phone shows just its icon). */
function Item({ x }: { x: HistoryItem }) {
  return (
    <li>
      <a className="hist-item" href={explorerTx(x.tx)} target="_blank" rel="noopener noreferrer">
        <span className="hist-item__what"><strong>{x.action}</strong><small>{x.timestamp ? time(x.timestamp) : `block ${x.block}`}</small></span>
        <span className="hist-item__amount">
          <AssetIcon symbol={x.symbol} size={22} />
          {x.amount !== null ? <span>{SIGN[x.action]}{fmtToken(x.amount, x.decimals, x.decimals === 6 ? 2 : 4)} {x.symbol}</span> : <span>{x.symbol}</span>}
          {x.secondary && <span className="muted">for {fmtToken(x.secondary.amount, x.secondary.decimals, 2)} {x.secondary.symbol} of debt</span>}
        </span>
        <span className="btn btn--outlined btn--sm hist-item__view"><span className="hist-item__view-text">View</span><IconExternal size={12} /></span>
      </a>
    </li>
  );
}

export function History() {
  const { address, status } = useWallet();
  const { openConnect } = useModals();
  const q = useHistory(address);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const groups = useMemo(() => {
    const items = (q.data ?? []).filter((x) => filter === 'All' || x.action === filter);
    const out = new Map<string, HistoryItem[]>();
    for (const x of items) { const k = x.timestamp ? day(x.timestamp) : 'Earlier'; out.set(k, [...(out.get(k) ?? []), x]); }
    return [...out.entries()];
  }, [q.data, filter]);

  return (
    <>
      <TopBand title={<div className="market-title"><div><h1>Transaction history</h1></div></div>} />
      <main className="page page--narrow">
        {status !== 'connected' ? (
          <section className="paper connect-card"><h2>Connect your wallet</h2><p className="muted">Connect your wallet to see your transaction history in this market.</p><button type="button" className="btn btn--gradient btn--lg" onClick={openConnect}>Connect wallet</button></section>
        ) : (
          <Paper id="history" title="Transactions"
            right={<div className="segmented segmented--scroll" role="group" aria-label="Filter by action">{FILTERS.map((f) => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>{f}</button>)}</div>}>
            {q.isLoading && <div className="skeleton" style={{ height: 180 }} />}
            {q.isError && <Alert tone="critical" action={<button type="button" className="btn btn--outlined btn--sm" onClick={() => void q.refetch()}>Retry</button>}>The history could not be loaded from the chain.</Alert>}
            {q.data && groups.length === 0 && <p className="no-data">{filter === 'All' ? 'No transactions yet.' : `No ${filter.toLowerCase()} transactions yet.`}</p>}
            {groups.map(([label, items]) => (
              <div key={label} className="hist-group"><h3>{label}</h3><ul>{items.map((x) => <Item key={x.id} x={x} />)}</ul></div>
            ))}
            {q.data && q.data.length > 0 && <p className="muted small">Read directly from the pool&apos;s event logs. Collateral switches are listed under All.</p>}
          </Paper>
        )}
      </main>
    </>
  );
}
